import {
  APPOINTMENT_STATUS_LABELS,
  ASSIGNMENT_ROLE_LABELS,
  ASSIGNMENT_STATUS_LABELS,
  type Appointment,
  type AssignmentRole,
  type CandidatesResponse,
  HANDOVER_TYPE_LABELS,
  type HandoverType,
  SCHEDULE_CONFLICT_MESSAGES,
  type ScheduleResponse,
  appointmentSchema,
  candidatesResponseSchema,
  scheduleResponseSchema,
} from '@sivan/shared';
import { askConfirm, askText } from '../ui/confirm';
import { useId, useState } from 'react';
import { ApiError, NetworkError, apiGet, apiPost, apiPut } from '../api/client';
import { useCurrentProject } from '../current-project';
import { LoadingBlock, Problem } from '../handover/common';
import { Modal } from '../master-data/Modal';
import { useLoad, useWorkspace } from '../master-data/use-master-data';
import { useNarrowScreen } from '../master-data/use-narrow-screen';
import { navigate } from '../routing';
import {
  type CalendarView,
  DAY_NAMES,
  DEFAULT_FILTERS,
  type Filters,
  applyFilters,
  byDay,
  dateOf,
  dayKey,
  endTimeOf,
  pendingOf,
  primaryOf,
  rangeFor,
  step,
  timeOf,
  title,
  toInstant,
} from './calendar';

const NO_ACCESS = 'אין לך הרשאה לצפות ביומן המסירות של פרויקט זה.';
const DURATIONS = [30, 45, 60, 90, 120, 180, 240] as const;

/** A Hebrew explanation of a failed calendar action. */
export function explainScheduleError(error: unknown): string {
  if (error instanceof ApiError) {
    const conflict = error.body['conflict'] as { code?: string; details?: unknown } | undefined;
    if (error.status === 409 && typeof conflict?.code === 'string') {
      const base = SCHEDULE_CONFLICT_MESSAGES[conflict.code] ?? 'הפעולה מתנגשת בנתונים קיימים.';
      const details = Array.isArray(conflict.details) ? conflict.details : [];
      const overlaps = details.flatMap((d) =>
        typeof d === 'object' && d !== null && 'employee' in d
          ? [d as { employee: string; startsAt: string | null; otherProject: boolean }]
          : [],
      );
      const extra = details
        .filter((d): d is string => typeof d === 'string' && d !== conflict.code)
        .map((d) => SCHEDULE_CONFLICT_MESSAGES[d])
        .filter((m) => m !== undefined);
      return [
        base,
        ...overlaps.map((o) =>
          o.otherProject || o.startsAt === null
            ? `${o.employee}: משובץ בפרויקט אחר באותו זמן.`
            : `${o.employee}: משובץ גם ב-${dateOf(o.startsAt)} ${timeOf(o.startsAt)}.`,
        ),
        ...extra,
      ].join(' ');
    }
    if (error.status === 403) return 'אין לך הרשאה לפעולה זו.';
    if (error.status === 404) return 'המועד לא נמצא. ייתכן שנמחק מהתצוגה; טענו את היומן מחדש.';
    if (error.status === 422) return 'חלק מהערכים אינם תקינים. בדקו ונסו שוב.';
  }
  if (error instanceof NetworkError) return 'אין חיבור לשרת. הפעולה לא בוצעה; נסו שוב.';
  return 'הפעולה נכשלה. נסו שוב.';
}

function EventCard({
  appointment,
  onOpen,
}: {
  readonly appointment: Appointment;
  readonly onOpen: (id: string) => void;
}) {
  const primary = primaryOf(appointment);
  const pending = pendingOf(appointment).length;
  return (
    <button
      type="button"
      className={`event event--${appointment.type} event--${appointment.status}`}
      onClick={() => {
        onOpen(appointment.id);
      }}
    >
      <span className="event__time">
        {timeOf(appointment.startsAt)}–
        {endTimeOf(appointment.startsAt, appointment.durationMinutes)}
      </span>
      <span className="event__title">
        דירה {appointment.apartment.number} · בניין {appointment.apartment.building}
      </span>
      <span className="event__meta">
        {HANDOVER_TYPE_LABELS[appointment.type]}
        {appointment.status !== 'planned' && ` · ${APPOINTMENT_STATUS_LABELS[appointment.status]}`}
      </span>
      <span className={`event__who${primary === undefined ? ' event__who--none' : ''}`}>
        {primary === undefined ? 'ללא אחראי' : primary.employee.fullName}
      </span>
      {pending > 0 && (
        <span className="chip chip--pending">
          {pending === 1 ? 'בקשה ממתינה' : `${String(pending)} בקשות ממתינות`}
        </span>
      )}
    </button>
  );
}

function Agenda({
  days,
  appointments,
  onOpen,
}: {
  readonly days: readonly Date[];
  readonly appointments: readonly Appointment[];
  readonly onOpen: (id: string) => void;
}) {
  const grouped = byDay(appointments);
  const shown = days.filter((d) => grouped.has(dayKey(d)));
  if (shown.length === 0) return <p className="empty">אין מסירות מתוכננות בטווח זה.</p>;
  return (
    <ol className="agenda">
      {shown.map((d) => (
        <li key={dayKey(d)} className="agenda__day">
          <h3 className="agenda__date">
            יום {DAY_NAMES[d.getDay()]} {dateOf(d.toISOString())}
          </h3>
          <ul className="agenda__events">
            {(grouped.get(dayKey(d)) ?? []).map((a) => (
              <li key={a.id}>
                <EventCard appointment={a} onOpen={onOpen} />
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ol>
  );
}

function Grid({
  view,
  anchor,
  days,
  appointments,
  onOpen,
}: {
  readonly view: CalendarView;
  readonly anchor: Date;
  readonly days: readonly Date[];
  readonly appointments: readonly Appointment[];
  readonly onOpen: (id: string) => void;
}) {
  const grouped = byDay(appointments);
  const today = dayKey(new Date());
  return (
    <div className={`calendar-grid calendar-grid--${view}`} role="grid" aria-label="יומן מסירות">
      <div className="calendar-grid__head" role="row">
        {DAY_NAMES.map((name) => (
          <span key={name} role="columnheader" className="calendar-grid__dayname">
            {name}
          </span>
        ))}
      </div>
      {Array.from({ length: days.length / 7 }, (_, week) => (
        <div key={week} className="calendar-grid__week" role="row">
          {days.slice(week * 7, week * 7 + 7).map((d) => {
            const list = grouped.get(dayKey(d)) ?? [];
            const outside = view === 'month' && d.getMonth() !== anchor.getMonth();
            return (
              <div
                key={dayKey(d)}
                role="gridcell"
                aria-label={`${DAY_NAMES[d.getDay()] ?? ''} ${dateOf(d.toISOString())}`}
                className={`calendar-grid__cell${outside ? ' calendar-grid__cell--outside' : ''}${dayKey(d) === today ? ' calendar-grid__cell--today' : ''}`}
              >
                <span className="calendar-grid__date">{d.getDate()}</span>
                {list.map((a) => (
                  <EventCard key={a.id} appointment={a} onOpen={onOpen} />
                ))}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function AppointmentForm({
  apartments,
  initial,
  submitLabel,
  editing,
  onSubmit,
  onCancel,
}: {
  readonly apartments: ScheduleResponse['apartments'];
  readonly initial: {
    apartmentId: string;
    type: HandoverType;
    day: string;
    time: string;
    duration: number;
    status: 'planned' | 'confirmed' | 'completed';
    notes: string;
  };
  readonly submitLabel: string;
  /** Editing an existing appointment: the apartment and type are fixed. */
  readonly editing: boolean;
  readonly onSubmit: (values: typeof initial) => Promise<void>;
  readonly onCancel: () => void;
}) {
  const [values, setValues] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (change: Partial<typeof initial>) => {
    setValues((v) => ({ ...v, ...change }));
  };
  return (
    <form
      className="appointment-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (values.apartmentId === '') {
          setError('יש לבחור דירה.');
          return;
        }
        setBusy(true);
        setError(null);
        onSubmit(values).then(
          () => {
            setBusy(false);
          },
          (failure: unknown) => {
            setBusy(false);
            setError(explainScheduleError(failure));
          },
        );
      }}
    >
      {!editing && (
        <>
          <label className="field">
            <span>דירה</span>
            <select
              value={values.apartmentId}
              onChange={(e) => {
                set({ apartmentId: e.target.value });
              }}
            >
              <option value="">בחירת דירה</option>
              {apartments.map((a) => (
                <option key={a.id} value={a.id}>
                  בניין {a.building} · דירה {a.number}
                </option>
              ))}
            </select>
          </label>
          <fieldset className="field choice-row">
            <legend>סוג המסירה</legend>
            {(['initial', 'final'] as const).map((type) => (
              <label key={type} className="choice">
                <input
                  type="radio"
                  name="type"
                  checked={values.type === type}
                  onChange={() => {
                    set({ type });
                  }}
                />
                {HANDOVER_TYPE_LABELS[type]}
              </label>
            ))}
          </fieldset>
        </>
      )}
      <div className="appointment-form__when">
        <label className="field">
          <span>תאריך</span>
          <input
            type="date"
            required
            value={values.day}
            onChange={(e) => {
              set({ day: e.target.value });
            }}
          />
        </label>
        <label className="field">
          <span>שעת התחלה</span>
          <input
            type="time"
            required
            step={300}
            value={values.time}
            onChange={(e) => {
              set({ time: e.target.value });
            }}
          />
        </label>
        <label className="field">
          <span>משך משוער</span>
          <select
            value={values.duration}
            onChange={(e) => {
              set({ duration: Number(e.target.value) });
            }}
          >
            {DURATIONS.map((m) => (
              <option key={m} value={m}>
                {m < 60 ? `${String(m)} דקות` : `${String(m / 60)} שעות`}
              </option>
            ))}
          </select>
        </label>
      </div>
      {editing && (
        <label className="field">
          <span>סטטוס</span>
          <select
            value={values.status}
            onChange={(e) => {
              set({ status: e.target.value as typeof values.status });
            }}
          >
            {(['planned', 'confirmed', 'completed'] as const).map((s) => (
              <option key={s} value={s}>
                {APPOINTMENT_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </label>
      )}
      <label className="field">
        <span>הערות פנימיות (רשות)</span>
        <textarea
          rows={2}
          maxLength={1000}
          value={values.notes}
          onChange={(e) => {
            set({ notes: e.target.value });
          }}
        />
      </label>
      {error !== null && (
        <p className="notice notice--error" role="alert">
          {error}
        </p>
      )}
      <div className="form-actions">
        <button type="submit" className="button button--primary" disabled={busy}>
          {busy ? 'שומר…' : submitLabel}
        </button>
        <button type="button" className="button" onClick={onCancel}>
          ביטול
        </button>
      </div>
    </form>
  );
}

function AssignForm({
  projectId,
  appointmentId,
  onAssign,
}: {
  readonly projectId: string;
  readonly appointmentId: string;
  readonly onAssign: (employeeId: string, role: AssignmentRole) => Promise<void>;
}) {
  const [candidates] = useLoad(`candidates:${appointmentId}`, () =>
    apiGet(
      `/projects/${projectId}/schedule/appointments/${appointmentId}/candidates`,
      candidatesResponseSchema,
    ),
  );
  const [role, setRole] = useState<AssignmentRole>('primary');
  const [employeeId, setEmployeeId] = useState('');
  const [busy, setBusy] = useState(false);
  if (candidates.state === 'loading') return <LoadingBlock />;
  if (candidates.state !== 'ready')
    return <p className="notice notice--error">טעינת רשימת העובדים נכשלה.</p>;
  const reasons = (item: CandidatesResponse['items'][number]) =>
    role === 'primary' ? item.primary : item.participant;
  return (
    <form
      className="assign-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (employeeId === '') return;
        setBusy(true);
        void onAssign(employeeId, role).finally(() => {
          setBusy(false);
        });
      }}
    >
      <fieldset className="field choice-row">
        <legend>תפקיד במסירה</legend>
        {(['primary', 'participant'] as const).map((r) => (
          <label key={r} className="choice">
            <input
              type="radio"
              name="role"
              checked={role === r}
              onChange={() => {
                setRole(r);
                setEmployeeId('');
              }}
            />
            {ASSIGNMENT_ROLE_LABELS[r]}
          </label>
        ))}
      </fieldset>
      {candidates.data.items.length === 0 ? (
        <p className="empty">אין עובדים בצוות הפרויקט. הוסיפו אותם במסך "צוות והרשאות".</p>
      ) : (
        <ul className="candidate-list" aria-label="עובדים בצוות הפרויקט">
          {candidates.data.items.map((item) => {
            const why = reasons(item);
            return (
              <li key={item.employee.id}>
                <label className={`candidate${why.length > 0 ? ' candidate--blocked' : ''}`}>
                  <input
                    type="radio"
                    name="employee"
                    disabled={why.length > 0}
                    checked={employeeId === item.employee.id}
                    onChange={() => {
                      setEmployeeId(item.employee.id);
                    }}
                  />
                  <span className="candidate__name">{item.employee.fullName}</span>
                  <span className="hint">{item.responsibility}</span>
                  {why.length > 0 && (
                    <span className="candidate__why">
                      {why.map((w) => SCHEDULE_CONFLICT_MESSAGES[w] ?? w).join(' ')}
                    </span>
                  )}
                </label>
              </li>
            );
          })}
        </ul>
      )}
      <button type="submit" className="button button--primary" disabled={busy || employeeId === ''}>
        {busy ? 'משבץ…' : `שיבוץ כ${ASSIGNMENT_ROLE_LABELS[role]}`}
      </button>
    </form>
  );
}

function AppointmentDetails({
  projectId,
  appointment,
  data,
  onChanged,
  onClose,
}: {
  readonly projectId: string;
  readonly appointment: Appointment;
  readonly data: ScheduleResponse;
  readonly onChanged: () => void;
  readonly onClose: () => void;
}) {
  const [mode, setMode] = useState<'view' | 'edit' | 'cancel' | 'assign'>('view');
  const [message, setMessage] = useState<{ kind: 'error' | 'done'; text: string } | null>(null);
  const [reason, setReason] = useState('');
  const { permissions, me } = data;
  const base = `/projects/${projectId}/schedule`;
  const open = ['planned', 'confirmed'].includes(appointment.status);
  const mine = appointment.assignments.find(
    (s) => s.employee.id === me?.employeeId && ['requested', 'assigned'].includes(s.status),
  );
  const hasPrimary = primaryOf(appointment) !== undefined;
  const run = async (action: () => Promise<unknown>, done: string) => {
    setMessage(null);
    try {
      await action();
      setMessage({ kind: 'done', text: done });
      setMode('view');
      onChanged();
    } catch (error) {
      setMessage({ kind: 'error', text: explainScheduleError(error) });
    }
  };
  const post = (path: string, body: object) => apiPost(`${base}${path}`, body, appointmentSchema);
  const local = new Date(appointment.startsAt);
  return (
    <div className="appointment-details">
      <dl className="details">
        <div>
          <dt>דירה</dt>
          <dd>
            בניין {appointment.apartment.building} · דירה {appointment.apartment.number}
            {appointment.apartment.floor !== '' && ` · ${appointment.apartment.floor}`}
          </dd>
        </div>
        <div>
          <dt>סוג</dt>
          <dd>{HANDOVER_TYPE_LABELS[appointment.type]}</dd>
        </div>
        <div>
          <dt>מועד</dt>
          <dd>
            {dateOf(appointment.startsAt)} · {timeOf(appointment.startsAt)}–
            {endTimeOf(appointment.startsAt, appointment.durationMinutes)}
          </dd>
        </div>
        <div>
          <dt>סטטוס</dt>
          <dd>
            <span className={`chip chip--appointment-${appointment.status}`}>
              {APPOINTMENT_STATUS_LABELS[appointment.status]}
            </span>
            {appointment.cancelReason !== null && ` · ${appointment.cancelReason}`}
          </dd>
        </div>
        {appointment.notes !== null && (
          <div>
            <dt>הערות פנימיות</dt>
            <dd>{appointment.notes}</dd>
          </div>
        )}
      </dl>
      <button
        type="button"
        className="button"
        onClick={() => {
          onClose();
          navigate(
            `#handover/${projectId}/a/${appointment.apartment.id}${appointment.handoverId === null ? '' : `/h/${appointment.handoverId}`}`,
            'push',
          );
        }}
      >
        {appointment.handoverId === null ? 'מעבר לדירה במסירות' : 'פתיחת טיוטת המסירה'}
      </button>

      <section className="staff" aria-label="צוות המסירה">
        <h3 className="staff__title">צוות המסירה</h3>
        {appointment.assignments.length === 0 ? (
          <p className="empty">טרם שובצו עובדים.</p>
        ) : (
          <ul className="staff__list">
            {appointment.assignments.map((s) => (
              <li key={s.id} className={`staff__item staff__item--${s.status}`}>
                <span className="staff__name">{s.employee.fullName}</span>
                <span className="chip">{ASSIGNMENT_ROLE_LABELS[s.role]}</span>
                <span className={`chip chip--assignment-${s.status}`}>
                  {ASSIGNMENT_STATUS_LABELS[s.status]}
                </span>
                <span className="hint staff__by">
                  {s.direct
                    ? `שובץ ישירות ע"י ${s.requestedBy}`
                    : s.selfRequested
                      ? 'ביקש/ה בעצמו/ה'
                      : `התבקש ע"י ${s.requestedBy}`}
                  {!s.direct && s.decidedBy !== null
                    ? ` · ${s.status === 'rejected' ? 'נדחה' : 'אושר'} ע"י ${s.decidedBy}`
                    : ''}
                  {s.decisionNote !== null && ` · ${s.decisionNote}`}
                  {s.cancelledBy !== null && ` · בוטל ע"י ${s.cancelledBy}`}
                </span>
                {open && s.status === 'requested' && permissions.approve && (
                  <span className="staff__actions">
                    <button
                      type="button"
                      className="button button--small button--primary"
                      aria-label={`אישור הבקשה של ${s.employee.fullName}`}
                      onClick={() => {
                        void run(
                          () => post(`/assignments/${s.id}/approve`, { note: null }),
                          'הבקשה אושרה.',
                        );
                      }}
                    >
                      אישור
                    </button>
                    <button
                      type="button"
                      className="button button--small"
                      aria-label={`דחיית הבקשה של ${s.employee.fullName}`}
                      onClick={() => {
                        // Cancelling the question cancels the rejection.
                        void askText({
                          title: `לדחות את הבקשה של ${s.employee.fullName}?`,
                          label: 'סיבת הדחייה',
                          optional: true,
                          maxLength: 500,
                          confirmLabel: 'דחיית הבקשה',
                          danger: true,
                        }).then((note) => {
                          if (note === null) return;
                          void run(
                            () =>
                              post(`/assignments/${s.id}/reject`, {
                                note: note === '' ? null : note,
                              }),
                            'הבקשה נדחתה.',
                          );
                        });
                      }}
                    >
                      דחייה
                    </button>
                  </span>
                )}
                {['requested', 'assigned'].includes(s.status) &&
                  (permissions.assign ||
                    (s.status === 'requested' && s.employee.id === me?.employeeId)) && (
                    <button
                      type="button"
                      className="button button--small button--danger"
                      aria-label={
                        s.status === 'requested' && s.employee.id === me?.employeeId
                          ? 'ביטול הבקשה שלי'
                          : `הסרת ${s.employee.fullName} מהמסירה`
                      }
                      onClick={() => {
                        void askConfirm({
                          title: `להסיר את ${s.employee.fullName} מהמסירה?`,
                          confirmLabel: 'הסרה מהמסירה',
                          danger: true,
                        }).then((yes) => {
                          if (yes)
                            void run(
                              () => post(`/assignments/${s.id}/cancel`, { reason: null }),
                              'השיבוץ בוטל.',
                            );
                        });
                      }}
                    >
                      {s.status === 'requested' && s.employee.id === me?.employeeId
                        ? 'ביטול הבקשה'
                        : 'הסרה'}
                    </button>
                  )}
              </li>
            ))}
          </ul>
        )}
        {open && permissions.request && me !== null && mine === undefined && (
          <div className="staff__request">
            {!hasPrimary && (
              <button
                type="button"
                className="button button--primary"
                onClick={() => {
                  void run(
                    () => post(`/appointments/${appointment.id}/requests`, { role: 'primary' }),
                    'הבקשה נשלחה לאישור.',
                  );
                }}
              >
                בקשה לשיבוץ כאחראי
              </button>
            )}
            <button
              type="button"
              className="button"
              onClick={() => {
                void run(
                  () => post(`/appointments/${appointment.id}/requests`, { role: 'participant' }),
                  'הבקשה נשלחה לאישור.',
                );
              }}
            >
              בקשה להצטרף כמלווה
            </button>
          </div>
        )}
      </section>

      {message !== null && (
        <p
          className={`notice ${message.kind === 'error' ? 'notice--error' : 'notice--success'}`}
          role={message.kind === 'error' ? 'alert' : 'status'}
        >
          {message.text}
        </p>
      )}

      {open && mode === 'view' && (permissions.assign || permissions.manage) && (
        <div className="form-actions">
          {permissions.assign && (
            <button
              type="button"
              className="button"
              onClick={() => {
                setMode('assign');
                setMessage(null);
              }}
            >
              שיבוץ עובד
            </button>
          )}
          {permissions.manage && (
            <>
              <button
                type="button"
                className="button"
                onClick={() => {
                  setMode('edit');
                  setMessage(null);
                }}
              >
                עריכה ושינוי מועד
              </button>
              <button
                type="button"
                className="button button--danger"
                onClick={() => {
                  setMode('cancel');
                  setMessage(null);
                }}
              >
                ביטול המועד
              </button>
            </>
          )}
        </div>
      )}
      {mode === 'assign' && (
        <AssignForm
          projectId={projectId}
          appointmentId={appointment.id}
          onAssign={(employeeId, role) =>
            run(
              () => post(`/appointments/${appointment.id}/assignments`, { employeeId, role }),
              'העובד שובץ.',
            )
          }
        />
      )}
      {mode === 'edit' && (
        <AppointmentForm
          apartments={data.apartments}
          editing
          submitLabel="שמירת השינויים"
          initial={{
            apartmentId: appointment.apartment.id,
            type: appointment.type,
            day: dayKey(local),
            time: timeOf(appointment.startsAt),
            duration: appointment.durationMinutes,
            status: appointment.status === 'cancelled' ? 'planned' : appointment.status,
            notes: appointment.notes ?? '',
          }}
          onCancel={() => {
            setMode('view');
          }}
          onSubmit={async (values) => {
            await apiPut(
              `${base}/appointments/${appointment.id}`,
              {
                rowVersion: appointment.rowVersion,
                startsAt: toInstant(values.day, values.time),
                durationMinutes: values.duration,
                status: values.status,
                notes: values.notes.trim() === '' ? null : values.notes,
              },
              appointmentSchema,
            );
            setMessage({ kind: 'done', text: 'המועד עודכן.' });
            setMode('view');
            onChanged();
          }}
        />
      )}
      {mode === 'cancel' && (
        <form
          className="cancel-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (reason.trim() === '') return;
            void run(
              () =>
                post(`/appointments/${appointment.id}/cancel`, {
                  rowVersion: appointment.rowVersion,
                  reason: reason.trim(),
                }),
              'המועד בוטל.',
            );
          }}
        >
          <label className="field">
            <span>סיבת הביטול</span>
            <textarea
              rows={2}
              required
              maxLength={300}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
              }}
            />
          </label>
          <p className="hint">ביטול הוא סופי. בקשות ממתינות יבוטלו, והשיבוצים יישמרו בהיסטוריה.</p>
          <div className="form-actions">
            <button type="submit" className="button button--danger" disabled={reason.trim() === ''}>
              ביטול המועד
            </button>
            <button
              type="button"
              className="button"
              onClick={() => {
                setMode('view');
              }}
            >
              חזרה
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function ProjectSchedule({ projectId }: { readonly projectId: string }) {
  const narrow = useNarrowScreen();
  const [view, setView] = useState<CalendarView>('week');
  const [anchor, setAnchor] = useState(() => new Date());
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [version, setVersion] = useState(0);
  const filtersId = useId();
  const { days, from, to } = rangeFor(view, anchor);
  const [data, reload] = useLoad(
    `schedule:${projectId}:${from.toISOString()}:${String(version)}`,
    () =>
      apiGet(
        `/projects/${projectId}/schedule?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`,
        scheduleResponseSchema,
      ),
  );
  const changed = () => {
    setVersion((v) => v + 1);
  };
  // After a change the calendar reloads in place: the open appointment stays open meanwhile.
  const [last, setLast] = useState<{ key: string; data: ScheduleResponse } | null>(null);
  const rangeKey = `${projectId}:${from.toISOString()}`;
  // Remembered from the previous render (React's pattern for state derived from earlier renders).
  if (data.state === 'ready' && last?.data !== data.data)
    setLast({ key: rangeKey, data: data.data });
  const shown =
    data.state === 'ready'
      ? data.data
      : data.state === 'loading' && last?.key === rangeKey
        ? last.data
        : null;

  const toolbar = (
    <div className="calendar-toolbar">
      <div className="segmented" role="group" aria-label="תצוגה">
        {(['day', 'week', 'month'] as const).map((v) => (
          <button
            key={v}
            type="button"
            className="segmented__option"
            aria-pressed={view === v}
            onClick={() => {
              setView(v);
            }}
          >
            {v === 'day' ? 'יום' : v === 'week' ? 'שבוע' : 'חודש'}
          </button>
        ))}
      </div>
      <div className="calendar-toolbar__nav">
        <button
          type="button"
          className="button"
          aria-label="הקודם"
          onClick={() => {
            setAnchor((a) => step(view, a, -1));
          }}
        >
          →
        </button>
        <button
          type="button"
          className="button"
          onClick={() => {
            setAnchor(new Date());
          }}
        >
          היום
        </button>
        <button
          type="button"
          className="button"
          aria-label="הבא"
          onClick={() => {
            setAnchor((a) => step(view, a, 1));
          }}
        >
          ←
        </button>
      </div>
      <h2 className="calendar-toolbar__title" aria-live="polite">
        {title(view, anchor)}
      </h2>
    </div>
  );

  if (data.state === 'forbidden') return <Problem text={NO_ACCESS} />;
  if (data.state === 'error')
    return (
      <>
        {toolbar}
        <Problem text="טעינת היומן נכשלה." requestId={data.requestId} onRetry={reload} />
      </>
    );
  if (shown === null)
    return (
      <>
        {toolbar}
        <LoadingBlock />
      </>
    );
  const schedule = shown;
  const me = schedule.me?.employeeId ?? null;
  const visible = applyFilters(schedule.appointments, filters, me);
  const pending = schedule.appointments.filter(
    (a) => ['planned', 'confirmed'].includes(a.status) && pendingOf(a).length > 0,
  );
  const buildings = [...new Set(schedule.appointments.map((a) => a.apartment.building))].sort();
  const opened = schedule.appointments.find((a) => a.id === openId);

  return (
    <div className="schedule">
      {toolbar}
      <div className="calendar-filters" role="group" aria-labelledby={filtersId}>
        <span id={filtersId} className="visually-hidden">
          סינון
        </span>
        <label className="field">
          <span>סוג</span>
          <select
            value={filters.type}
            onChange={(e) => {
              setFilters((f) => ({ ...f, type: e.target.value as Filters['type'] }));
            }}
          >
            <option value="all">כל הסוגים</option>
            <option value="initial">{HANDOVER_TYPE_LABELS.initial}</option>
            <option value="final">{HANDOVER_TYPE_LABELS.final}</option>
          </select>
        </label>
        <label className="field">
          <span>סטטוס</span>
          <select
            value={filters.status}
            onChange={(e) => {
              setFilters((f) => ({ ...f, status: e.target.value as Filters['status'] }));
            }}
          >
            <option value="active">פעילים (מתוכנן ומאושר)</option>
            <option value="all">הכול</option>
            {(['planned', 'confirmed', 'completed', 'cancelled'] as const).map((s) => (
              <option key={s} value={s}>
                {APPOINTMENT_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>שיבוץ</span>
          <select
            value={filters.who}
            onChange={(e) => {
              setFilters((f) => ({ ...f, who: e.target.value as Filters['who'] }));
            }}
          >
            <option value="all">כל המסירות</option>
            {me !== null && <option value="mine">המסירות שלי</option>}
            <option value="unassigned">ללא אחראי</option>
            <option value="pending">עם בקשות ממתינות</option>
          </select>
        </label>
        {buildings.length > 1 && (
          <label className="field">
            <span>בניין</span>
            <select
              value={filters.building}
              onChange={(e) => {
                setFilters((f) => ({ ...f, building: e.target.value }));
              }}
            >
              <option value="">כל הבניינים</option>
              {buildings.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
          </label>
        )}
        {schedule.permissions.manage && (
          <button
            type="button"
            className="button button--primary calendar-filters__new"
            onClick={() => {
              setCreating(true);
            }}
          >
            + מועד מסירה חדש
          </button>
        )}
      </div>

      {schedule.permissions.approve && pending.length > 0 && (
        <section className="pending notice notice--warning" aria-label="בקשות שיבוץ ממתינות">
          <h3 className="pending__title">
            {pending.length === 1
              ? 'מסירה אחת עם בקשת שיבוץ ממתינה'
              : `${String(pending.length)} מסירות עם בקשות שיבוץ ממתינות`}
          </h3>
          <ul className="pending__list">
            {pending.map((a) => (
              <li key={a.id}>
                <button
                  type="button"
                  className="button button--small"
                  onClick={() => {
                    setOpenId(a.id);
                  }}
                >
                  {dateOf(a.startsAt)} {timeOf(a.startsAt)} · דירה {a.apartment.number} ·{' '}
                  {pendingOf(a)
                    .map((s) => s.employee.fullName)
                    .join(', ')}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {narrow || view === 'day' ? (
        <Agenda days={days} appointments={visible} onOpen={setOpenId} />
      ) : (
        <>
          <Grid view={view} anchor={anchor} days={days} appointments={visible} onOpen={setOpenId} />
          {visible.length === 0 && <p className="empty">אין מסירות מתוכננות בטווח זה.</p>}
        </>
      )}

      <Modal
        title={
          opened === undefined
            ? 'מועד מסירה'
            : `${HANDOVER_TYPE_LABELS[opened.type]} · דירה ${opened.apartment.number}`
        }
        open={opened !== undefined}
        onClose={() => {
          setOpenId(null);
        }}
      >
        {opened !== undefined && (
          <AppointmentDetails
            key={opened.id}
            projectId={projectId}
            appointment={opened}
            data={schedule}
            onChanged={changed}
            onClose={() => {
              setOpenId(null);
            }}
          />
        )}
      </Modal>
      <Modal
        title="מועד מסירה חדש"
        open={creating}
        onClose={() => {
          setCreating(false);
        }}
      >
        {creating && (
          <AppointmentForm
            apartments={schedule.apartments}
            editing={false}
            submitLabel="קביעת המועד"
            initial={{
              apartmentId: '',
              type: 'initial',
              day: dayKey(view === 'month' ? new Date() : anchor),
              time: '09:00',
              duration: 90,
              status: 'planned',
              notes: '',
            }}
            onCancel={() => {
              setCreating(false);
            }}
            onSubmit={async (values) => {
              const created = await apiPost(
                `/projects/${projectId}/schedule/appointments`,
                {
                  apartmentId: values.apartmentId,
                  type: values.type,
                  startsAt: toInstant(values.day, values.time),
                  durationMinutes: values.duration,
                  notes: values.notes.trim() === '' ? null : values.notes,
                },
                appointmentSchema,
              );
              setCreating(false);
              setAnchor(new Date(created.startsAt));
              changed();
              setOpenId(created.id);
            }}
          />
        )}
      </Modal>
    </div>
  );
}

/** יומן מסירות: the handover calendar of one project (synthetic development data only). */
export function ScheduleScreen() {
  const [workspace, reload] = useWorkspace();
  const project = useCurrentProject(workspace.state === 'ready' ? workspace.data.projects : []);
  if (workspace.state === 'loading') return <LoadingBlock />;
  if (workspace.state === 'forbidden') return <Problem text={NO_ACCESS} />;
  if (workspace.state === 'error')
    return (
      <Problem text="טעינת הפרויקטים נכשלה." requestId={workspace.requestId} onRetry={reload} />
    );
  const { projects } = workspace.data;
  if (projects.length === 0) return <p className="empty">אין פרויקטים זמינים עבורך.</p>;
  if (project === undefined) return null;
  return (
    <div className="schedule-screen">
      <ProjectSchedule key={project.id} projectId={project.id} />
    </div>
  );
}
